<script setup lang="ts">
import useVuelidate from '@vuelidate/core';
import { useToast } from 'primevue/usetoast';
import Calendar from 'primevue/calendar';
import { StudentInfoFormInterface } from '@/types';
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { PersonStores } from '@/store';
import { can, useDateTime } from '@/composables';
import { PERMISSIONS, GRADES_ORDER, GradeKey } from '@/enum';

const toast = useToast();
const profileStore = PersonStores.useUserProfileStore();

const student = reactive<StudentInfoFormInterface>({
  grade: undefined,
  gender: undefined,
  age: undefined,
  birthday: undefined,
  comment: undefined,
  building_campus: undefined,
});

const genders = [
  { id: 'M', title: 'Male' },
  { id: 'F', title: 'Female' },
];

const grades = GRADES_ORDER as readonly GradeKey[];

//validator rules
const rules = computed(() => {
  return {
    birthday: { lazy: true },
    grade: {},
    gender: {},
    age: {},
    comment: {},
  };
});

const canUpdateStudentInfoDisabled = () => !can([PERMISSIONS.PEOPLE_UPDATE_STUDENT_INFORMATION]);

//set up the validator
const v$ = useVuelidate(rules, student);

const birthdayDate = ref();

const getDate = (date: string) => {
  const [year, month, day] = date.split('-').map((digit) => Number(digit));

  return new Date(year, month - 1, day);
};

const birthdayFormatted = computed(() => {
  if (!birthdayDate.value) return '';
  return birthdayDate.value.toJSON()?.split('T')[0];
});

onMounted(() => {
  setStudentInfo();
  birthdayDate.value = student.birthday ? getDate(student.birthday) : '';
});

/**
 * set the student info. if the student
 */
const setStudentInfo = () => {
  if (profileStore.studentInfo) {
    let { grade, birthday, gender, comment } = profileStore.studentInfo;
    student.grade = grade;
    birthdayDate.value = birthday ? getDate(birthday) : '';
    student.birthday = birthday;
    student.gender = gender;
    student.comment = comment;
  }
};

watch(() => profileStore.studentInfo, setStudentInfo);

/**
 * on cancel of the student Info form
 */
const onStudentInfoCancel = async () => (setStudentInfo(), resetDirty());

const onAttemptToSave = ref(false);

/**
 * update the student info
 */
const onStudentInfoSave = async () => {
  onAttemptToSave.value = true;
  v$.value.$touch();
  if (!v$.value.$invalid) {
    const result = await profileStore.updateStudentInfo(student);

    if (result === 200) {
      doToast('Student Information');
      resetDirty();
    }
  }
  return;
};

/**
 * show the toast
 *
 * @param formName
 */
const doToast = (formName: string) => {
  toast.add({
    severity: 'success',
    summary: 'Saved',
    detail: `${formName} has been saved`,
    life: 3000,
  });
};

const resetDirty = () => {
  if (v$) v$.value.$reset();
};

const fieldStyles = (key: string, styles?: string): string => {
  const validation = v$.value[key].$invalid && v$.value[key].$dirty ? 'p-invalid' : '';
  return `${styles} ${validation}`;
};

const maxDate = new Date();
maxDate.setDate(new Date().getDate() - 1);

watch(birthdayFormatted, () => {
  if (birthdayFormatted.value !== profileStore.studentInfo?.birthday) {
    student.birthday = birthdayFormatted.value;
    if (birthdayFormatted.value != '') v$.value.birthday.$touch();
  }
});

const verifyEmpty = (value: string | undefined) => {
  if (value?.length > 1) {
    return;
  }

  return v$.value['birthday'].$touch();
};
</script>

<template>
  <FormCard
    title="Student Information"
    :dirty="v$.$anyDirty"
    :cancel="onStudentInfoCancel"
    :save="onStudentInfoSave"
    :save-disabled="v$.$anyDirty && v$.$invalid"
  >
    <template #form>
      <form class="w-full">
        <div class="formgrid grid">
          <div class="md:col-6 col-12 field">
            <label for="grade">Grade</label>
            <Dropdown
              id="grade"
              v-model="v$.grade.$model"
              :options="grades"
              placeholder="Select a Grade"
              :class="fieldStyles('grade', 'w-full')"
              :disabled="canUpdateStudentInfoDisabled()"
            />
          </div>
          <div class="field md:col-6 col-12" :class="{ error: v$.gender.$errors.length }">
            <label for="gender">Gender</label>
            <Dropdown
              id="gender"
              v-model="v$.gender.$model"
              :options="genders"
              option-value="id"
              option-label="title"
              placeholder="Select a Gender"
              :class="fieldStyles('gender', 'w-full')"
              :disabled="canUpdateStudentInfoDisabled()"
            />
          </div>
        </div>
        <div class="formgrid grid">
          <div class="field col-10" :class="{ error: v$.birthday.$errors.length }">
            <label for="birthday">Birthdate</label>
            <Calendar
              id="birthday"
              v-model="birthdayDate"
              placeholder="mm/dd/yyyy"
              date-format="mm/dd/yy"
              :birthday="birthdayFormatted"
              :max-date="maxDate"
              :manual-input="true"
              :class="{ 'p-invalid': v$.birthday.$invalid && v$.birthday.$dirty }"
              :disabled="canUpdateStudentInfoDisabled()"
              @update:model-value="verifyEmpty(birthdayDate)"
            />
          </div>
          <div v-if="birthdayFormatted" class="w-2 mt-2 flex align-items-center justify-content-center">
            <label style="font-family: Rubik; font-size: 14px; width: auto">
              Age
              <b class="ml-2">{{ useDateTime().getAge(birthdayFormatted) }}</b>
            </label>
          </div>
        </div>
        <div class="field col-12 p-0" :class="{ error: v$.comment.$errors.length }">
          <label for="notes">Comments</label>
          <InputText
            id="notes"
            v-model="student.comment"
            class="inputfield w-full"
            :class="{ 'p-invalid': v$.comment.$invalid && v$.comment.$dirty }"
            :disabled="canUpdateStudentInfoDisabled()"
            @input="v$.comment.$touch"
          />
        </div>
      </form>
    </template>
  </FormCard>
</template>
